import * as Config from "config";
import Guid from "_shared/guids/guid";
import Message from "messaging/message";
import Topic from "messaging/forums/topics/topic";

/**
 * The browser blocks empty files and the API's total upper limit before upload. The floor only
 * refuses an empty file, as the API's does: a valid PDF starts around 300 bytes.
 */
export const MIN_MESSAGE_ATTACHMENT_BYTES = 1;
export const MAX_MESSAGE_ATTACHMENT_BYTES = 32 * 1024 * 1024;
export const MAX_MESSAGE_ATTACHMENTS = 10;
export const MAX_MESSAGE_ATTACHMENT_REQUEST_BYTES = 64 * 1024 * 1024;

export type MessagingValidationErrors = Readonly<Record<string, string>>;
type MessageValidationOptions = Readonly<{
  requireForumId?: boolean;
}>;

export function validateAttachment(file: File): string | undefined {
  const extension = file.name.includes(".") ? `.${file.name.split(".").pop()?.toLowerCase()}` : "";
  if (!Config.acceptedAttachmentFormats.includes(file.type) && !Config.acceptedAttachmentFormats.includes(extension)) {
    return "This file type is not supported.";
  }
  return validateAttachmentData(file.type, file.size, true);
}

function validateAttachmentData(type: string, size: number, typeAlreadyChecked = false): string | undefined {
  if (!typeAlreadyChecked && !Config.acceptedAttachmentFormats.includes(type)) {
    return "This file type is not supported.";
  }
  if (size < MIN_MESSAGE_ATTACHMENT_BYTES) {
    return "This file is empty. Choose a file with content.";
  }
  if (size > MAX_MESSAGE_ATTACHMENT_BYTES) {
    return "This file is too large. Choose a file no larger than 32 MB.";
  }
  return undefined;
}

export function validateAttachmentList(files: readonly File[]): MessagingValidationErrors {
  if (files.length > MAX_MESSAGE_ATTACHMENTS) {
    return { attachments: `You can attach no more than ${MAX_MESSAGE_ATTACHMENTS} files.` };
  }
  const total = files.reduce((sum, file) => sum + file.size, 0);
  if (total > MAX_MESSAGE_ATTACHMENT_REQUEST_BYTES) {
    return { attachments: "The total attachment size must be no larger than 64 MB." };
  }
  for (const file of files) {
    const error = validateAttachment(file);
    if (error) return { attachments: error };
  }
  return {};
}

export function validateTopic(topic: Topic | undefined): string | undefined {
  if (!topic?.entityClass || !topic.context?.trim()) return "A valid conversation topic is required.";
  if (topic.entityId && topic.entityId.isEmpty) return "A valid conversation topic is required.";
  return undefined;
}

export function validateMessage(
  message: Message,
  options: MessageValidationOptions = {},
): MessagingValidationErrors {
  const errors: Record<string, string> = {};
  if (options.requireForumId !== false && !message.forum?.id) {
    errors.forumId = "Message must have a forum id to be created";
  }
  if (!message.content?.trim() && message.attachments.length === 0) {
    errors.contentPlainText = "Message content is required when no attachments are provided.";
  }
  if (message.attachments.length > MAX_MESSAGE_ATTACHMENTS) {
    errors.attachments = `You can attach no more than ${MAX_MESSAGE_ATTACHMENTS} files.`;
  }
  const rawFiles = message.attachments
    .map((attachment) => attachment.rawData)
    .filter((rawData): rawData is Blob => typeof Blob !== "undefined" && rawData instanceof Blob);
  if (rawFiles.length > MAX_MESSAGE_ATTACHMENTS) {
    errors.attachments = `You can attach no more than ${MAX_MESSAGE_ATTACHMENTS} files.`;
  } else if (rawFiles.reduce((sum, file) => sum + file.size, 0) > MAX_MESSAGE_ATTACHMENT_REQUEST_BYTES) {
    errors.attachments = "The total attachment size must be no larger than 64 MB.";
  } else {
    for (const rawFile of rawFiles) {
      const attachmentError = validateAttachmentData(rawFile.type, rawFile.size);
      if (attachmentError) {
        errors.attachments = attachmentError;
        break;
      }
    }
  }
  return errors;
}

export function validateGuid(value: Guid | string | undefined, field: string): string | undefined {
  const text = value instanceof Guid ? value.value : value;
  return text && Guid.isValid(text) ? undefined : `${field} is required.`;
}
